<template>
  <v-dialog :model-value="editHours" max-width="600" persistent>
    <div class="bg-white">
      <div class="flex justify-end">
        <button class="p-4" @click="closeDialog">
          <fa-icon icon="fa-regular fa-xmark-large" />
        </button>
      </div>
      <div class="max-w-2xl p-5 pt-0">
        <div class="px-2">
          <h1 class="text-center text-2xl font-bold">Edit hours</h1>
          <p class="text-center text-sm font-normal">
            Adjust monthly hours needed for each role.
          </p>
        </div>
        <div
          v-for="(role, index) in selectedRoles"
          :key="role.value"
          class="grid grid-cols-2 place-content-center md:divide-y"
        >
          <div
            v-show="uiStore.mobile || (!uiStore.mobile && index === 0)"
            class="text-vgmedturq col-span-1 grid pb-4 pt-7 text-xs font-bold md:col-span-2 md:grid-cols-2 md:px-7"
          >
            <p class="mb-4 uppercase">Roles</p>
            <p class="mb-4 uppercase md:text-right">Hours/mo</p>
          </div>
          <div class="grid md:col-span-3 md:grid-cols-2 md:p-7">
            <v-chip
              size="large"
              class="bg-vgsoftblue m-1 truncate py-3 text-black md:px-7"
            >
              <span class="truncate"> {{ role.value }}</span>
            </v-chip>
            <div class="flex flex-row items-center justify-end">
              <v-text-field
                v-if="hasAdminPermissionInCheckout"
                v-model="role.hours"
                density="compact"
                variant="outlined"
                class="orange-text w-20 flex-none font-normal"
                :color="vgTeal"
              ></v-text-field>
              <v-select
                v-else
                :model-value="role.hours"
                :items="hoursItems"
                class="orange-text w-24 flex-none"
                variant="outlined"
                density="compact"
                :rules="requiredRules"
                @update:model-value="updateHoursOnSelect(index, $event)"
              ></v-select>

              <p class="ml-2 pb-4"></p>
            </div>
          </div>
        </div>

        <div class="col-span-full flex flex-row-reverse justify-between">
          <vg-btn @click="updateHours()"> save </vg-btn>
          <vg-btn vg-type="secondary" class="invisible">
            Help me decide
          </vg-btn>
        </div>
      </div>
    </div>
  </v-dialog>
</template>
<script>
import { checkoutHoursItems } from '@/config';
import tailwind from 'tailwind.config';
import { mapStores, storeToRefs } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import { useUiStore } from '@/stores/ui';
import { useUserStore } from '@/stores/user';

export default {
  name: 'HoursEditor',
  props: {
    editHours: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['hoursChanged', 'closeEditHours'],
  data() {
    const { user } = storeToRefs(useUserStore());

    return {
      user,
      checkoutHoursItems,
      requiredRules: [
        (v) => (!!v && parseInt(v) > 9) || 'Value greater than 9 required',
      ],
      vgTeal: tailwind.theme.extend.colors.vgteal[500],
    };
  },
  computed: {
    ...mapStores(useUiStore, useCheckoutStore),
    hasAdminPermissionInCheckout() {
      return !!(
        this.user?.permissions.includes('admin') &&
        this.checkoutStore.matchingCriteria?.uuid
      );
    },
    hours() {
      return this.checkoutStore.matchingCriteria.hours ?? [];
    },
    selectedRoles() {
      return this.checkoutStore.selectedRoles?.map((role) => {
        return {
          ...role,
          hours: this.checkoutStore.matchingCriteria.hours?.[
            this.getIndexOf(role.id)
          ],
        };
      });
    },

    hoursItems() {
      return [
        ...checkoutHoursItems,
        ...this.selectedRoles
          .filter(
            (role) =>
              role.hours &&
              !this.checkoutHoursItems.find((item) => item.value === role.hours)
          )
          .map((role) => {
            return { title: role?.hours?.toString(), value: role?.hours };
          }),
      ];
    },
  },

  methods: {
    updateHoursOnSelect(index, value) {
      this.hours[index] = value;
    },
    async updateHours() {
      let hours =
        this.hours.length === 0
          ? Array(this.selectedRoles.length).fill(10)
          : [...this.hours];

      this.selectedRoles.forEach((role) => {
        hours[this.getIndexOf(role.id)] = parseInt(role.hours) || 0;
      });

      let error = null;

      hours.forEach((roleHours) => {
        if (roleHours < 10) {
          error = 'Value for each role can not be less than 10';
          return;
        }
      });
      if (error) {
        return this.$root.$snackbar.error(error);
      }

      await this.checkoutStore.updateMatchingCriteria({
        ...this.checkoutStore.matchingCriteria,
        hours,
      });
      this.$emit('hoursChanged');
    },
    getIndexOf(id) {
      return this.checkoutStore.matchingCriteria.rids.findIndex(
        (rid) => parseInt(rid) === id
      );
    },
    closeDialog() {
      this.$emit('closeEditHours');
    },
  },
};
</script>
